import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox, FormField, Section, Select } from '../../../components/EntityList';
import { useLabels } from '../../../hooks/useLabels';

/**
 * Entity options shown under the column table: how payments pick the due
 * (cuota), which keys find existing accounts, and the bill options (payment
 * from "Fecha de pago", notifications). How each
 * reference column is looked up lives in the table itself (StepMapping).
 */
export default function MappingOptions({ schema, mapping, links, onLinksChange, options, onOptionsChange }) {
	const { t } = useLabels();
	const setLink = (key, value) => onLinksChange({ ...links, [key]: { ...links[key], ...value } });

	return (
		<div className='tw:space-y-4'>
			{schema.dueStrategies && (
				<Section title={t('entity.due')} description={t('import.conceptHint')}>
					<div className='tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2'>
						<FormField id='due-strategy' label={t('import.dueStrategy')}>
							<Select
								id='due-strategy'
								value={links.due?.strategy || schema.defaultDueStrategy}
								onChange={(e) => setLink('due', { strategy: e.target.value })}
								options={schema.dueStrategies.map((s) => ({ value: s.key, label: t(s.labelKey) }))}
							/>
						</FormField>
						{(links.due?.strategy || schema.defaultDueStrategy) === 'month' && (
							<FormField id='due-month-source' label={t('import.monthSource')}>
								<Select
									id='due-month-source'
									value={links.due?.monthSource || 'date'}
									onChange={(e) => setLink('due', { monthSource: e.target.value })}
									options={[
										{ value: 'date', label: t('import.monthSource.date') },
										...(mapping.period ? [{ value: 'period', label: t('import.monthSource.period') }] : []),
									]}
								/>
							</FormField>
						)}
					</div>
				</Section>
			)}

			{schema.matchOptions && (
				<Section title={t('import.accountMatch')} description={t('import.accountMatchHelp')}>
					<div className='tw:flex tw:flex-col tw:gap-2'>
						{schema.matchOptions.map((option) => {
							const checked = (links.match || []).includes(option.key);
							return (
								<Checkbox
									key={option.key}
									label={`${t(option.labelKey)}${mapping[option.key] ? ` («${mapping[option.key]}»)` : ''}`}
									checked={checked}
									disabled={!mapping[option.key]}
									onChange={() =>
										onLinksChange({
											...links,
											match: checked
												? (links.match || []).filter((k) => k !== option.key)
												: [...(links.match || []), option.key],
										})
									}
								/>
							);
						})}
					</div>
					<FormField id='default-account-type' label={t('import.defaultAccountType')} className='tw:mt-4 tw:max-w-[280px]'>
						<Select
							id='default-account-type'
							value={options.defaultAccountType}
							onChange={(e) => onOptionsChange({ ...options, defaultAccountType: e.target.value })}
							options={['Persona', 'Empresa']}
						/>
					</FormField>
				</Section>
			)}

			{schema.key === 'bill' && (
				<div className='tw:flex tw:flex-col tw:gap-2'>
					{(mapping.confirmationDate || mapping.paid) && (
						<Checkbox
							label={t('import.registerPayment')}
							checked={options.registerPayment !== false}
							onChange={() => onOptionsChange({ ...options, registerPayment: options.registerPayment === false })}
						/>
					)}
					<Checkbox
						label={t('import.notifyBills')}
						checked={!!options.notify}
						onChange={() => onOptionsChange({ ...options, notify: !options.notify })}
					/>
				</div>
			)}
		</div>
	);
}

MappingOptions.propTypes = {
	schema: PropTypes.object.isRequired,
	mapping: PropTypes.object.isRequired,
	links: PropTypes.object.isRequired,
	onLinksChange: PropTypes.func.isRequired,
	options: PropTypes.object.isRequired,
	onOptionsChange: PropTypes.func.isRequired,
};
